"use client";

import { useEffect, useState } from "react";

export interface SpondProfileSummary {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirthKnown?: boolean;
  isAdult?: boolean;
}

type ProfileLoadResult =
  | { kind: "ok"; profile: SpondProfileSummary }
  | { kind: "error"; status: number | null };

async function loadSpondProfile(basePath: string): Promise<ProfileLoadResult> {
  try {
    const res = await fetch(`${basePath}/api/profile`);
    if (!res.ok) return { kind: "error", status: res.status };
    return { kind: "ok", profile: await res.json() };
  } catch {
    return { kind: "error", status: null };
  }
}

/**
 * Loads the logged-in user's name/phone from `/api/profile` (the server-only
 * proxy that reads the session cookie). Pass `override` to skip the fetch
 * (tests, or when the data is already known). Shared by the adult-confirm and
 * child-guardian steps so the fetch + race-guard live in one place.
 *
 * `error` is true only when a fetch was attempted and failed — distinct from
 * a successful response that simply has no phone / no date of birth. `status`
 * carries the HTTP status of that failure (`null` on a network/transport
 * error, which never reached a response) — callers that need to tell "no
 * session" (401) apart from a genuine backend fault (502/network) read this
 * instead of just `error`.
 */
export function useSpondProfile(override?: SpondProfileSummary): {
  profile?: SpondProfileSummary;
  loading: boolean;
  error: boolean;
  status: number | null;
} {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const [fetched, setFetched] = useState<SpondProfileSummary | null>(null);
  const [loading, setLoading] = useState(override === undefined);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<number | null>(null);

  useEffect(() => {
    if (override !== undefined) return;
    let active = true;
    void loadSpondProfile(basePath).then((result) => {
      if (!active) return;
      if (result.kind === "ok") {
        setFetched(result.profile);
        setError(false);
        setStatus(null);
      } else {
        setError(true);
        setStatus(result.status);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [override, basePath]);

  if (override !== undefined) {
    return { profile: override, loading: false, error: false, status: null };
  }

  return { profile: fetched ?? undefined, loading, error, status };
}
